import type { PlanStatus, ScopeMode, TestResult, Teststufe } from '@testflow/shared';
import { ApiError, request } from './api.js';

export interface TestPlan {
  test_plan_id: string;
  project_id: string;
  name: string;
  teststufe: Teststufe;
  scope_mode: ScopeMode;
  window_from: string | null;
  target_date: string | null;
  // #114 (A9): ersetzt das frühere `is_active`. Ein Boolean konnte den Endzustand nicht
  // ausdrücken — „abgeschlossen" und „noch nie aktiviert" sahen identisch aus.
  status: PlanStatus;
  closed_at: string | null;
  closed_by: string | null;
  // Grund beim Abschließen trotz offener Bestätigungen bzw. beim Wiedereröffnen.
  closed_note: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlanItem {
  item_id: string;
  test_plan_id: string;
  test_case_id: string;
  in_scope: boolean;
  assigned_tester_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface TestPlanDetail extends TestPlan {
  items: PlanItem[];
}

export interface TestPlanCreateBody {
  name: string;
  teststufe: Teststufe;
  scope_mode?: ScopeMode;
  window_from?: string | null;
  target_date?: string | null;
}

export type TestPlanUpdateBody = Partial<TestPlanCreateBody>;

export interface AssignmentBody {
  test_case_id: string;
  tester_id: string | null;
}

export interface Suggestion {
  test_case_id: string;
  suggested_tester_id: string;
}

// „Vorschläge übernehmen" schreibt nur Vorschläge, deren Tester eine Rolle im Projekt hat und aktiv
// ist; alle anderen (typisch: Import-Platzhalter) kommen namentlich in `skipped` zurück.
export type SuggestionSkipReason = 'user_not_in_project' | 'user_inactive';
export interface SkippedSuggestion {
  test_case_id: string;
  human_id: string;
  title_de: string;
  title_en: string;
  tester_name: string;
  reason: SuggestionSkipReason;
}
export interface ApplySuggestionsResult {
  applied: number;
  skipped: SkippedSuggestion[];
}

// #114: die offenen Abnahme-Knoten aus der 409-Antwort von PATCH …/status. Der Server nennt sie
// beim Namen, damit das Warnfenster konkret werden kann, ohne die Regel „was zählt als offen"
// clientseitig ein zweites Mal zu bauen.
export interface OpenSignoffNode {
  node_id: string;
  node_name: string;
}

export interface ProjectMember {
  user_id: string;
  name: string;
  role: string;
}

export type PlanStatusKey = TestResult | 'open';

export interface OpenDefectRef {
  defect_id: string;
  human_id: string;
  status: string;
}

export interface LastRun {
  run_id: string;
  human_id: string;
  result: TestResult;
  executed_at: string;
  tester_name: string | null;
}

export interface PlanProgressItem {
  test_case_id: string;
  status: PlanStatusKey;
  runs_count: number;
  last_run: LastRun | null;
  // #35: projektweit je Testfall (nicht plan-gescopt) — ein offener Defect zählt unabhängig
  // davon, aus welchem Plan der auslösende Lauf kam.
  open_defects: OpenDefectRef[];
}

export interface PlanProgress {
  total: number;
  byStatus: Record<PlanStatusKey, number>;
  coveragePct: number;
  items: PlanProgressItem[];
}

export interface RunHistoryEntry {
  run_id: string;
  human_id: string;
  result: TestResult;
  result_description: string | null;
  executed_at: string;
  screenshots: string[];
  // #54 (EXE-7): beim Lauf gewählte Testdaten, bereits zu Art + Wert aufgelöst. Gelöschte Testdaten
  // sind serverseitig herausgefiltert; ein Lauf ohne Auswahl liefert eine leere Liste.
  used_test_data: {
    test_data_id: string;
    value: string;
    kind_label_de: string | null;
    kind_label_en: string | null;
  }[];
  tester: { user_id: string; name: string } | null;
  failed_step: {
    step_id: string;
    step_no: number;
    description_de: string;
    // #179: Anzeige in der Oberflaechensprache.
    description_en: string;
  } | null;
  // Erster Defect des Laufs (nach Anlage) — Rückwärtskompatibel neben `defects`.
  defect: { defect_id: string; human_id: string; status: string } | null;
  // #208 Nachtrag: alle Defects des Laufs (verknüpfte notierte Fehler + Auto-Defect), nach Anlage.
  defects: { defect_id: string; human_id: string; title: string; status: string }[];
}

const base = (p: string) => `/v1/projects/${p}`;

export const plansApi = {
  list: (p: string) => request<TestPlan[]>(`${base(p)}/plans`),
  get: (p: string, planId: string) => request<TestPlanDetail>(`${base(p)}/plans/${planId}`),
  create: (p: string, body: TestPlanCreateBody) =>
    request<TestPlan>(`${base(p)}/plans`, { method: 'POST', body: JSON.stringify(body) }),
  update: (p: string, planId: string, body: TestPlanUpdateBody) =>
    request<TestPlan>(`${base(p)}/plans/${planId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // #114 (A9): der Lebenszyklus. Eigener Aufruf statt eines Feldes in `update`, weil jeder
  // Übergang serverseitig an Bedingungen hängt (höchstens ein aktiver Plan, offene
  // Bestätigungen, Pflichtnotiz).
  setStatus: (
    p: string,
    planId: string,
    body: { status: PlanStatus; override_note?: string; reopen_note?: string },
  ) =>
    request<TestPlan>(`${base(p)}/plans/${planId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  remove: (p: string, planId: string) =>
    request<void>(`${base(p)}/plans/${planId}`, { method: 'DELETE' }),
  setScope: (p: string, planId: string, testCaseIds: string[]) =>
    request<{ ok: true; in_scope: number }>(`${base(p)}/plans/${planId}/scope`, {
      method: 'PUT',
      body: JSON.stringify({ test_case_ids: testCaseIds }),
    }),
  setAssignment: (p: string, planId: string, body: AssignmentBody) =>
    request<{ ok: true }>(`${base(p)}/plans/${planId}/assignments`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  suggestions: (p: string, planId: string) =>
    request<Suggestion[]>(`${base(p)}/plans/${planId}/suggestions`),
  applySuggestions: (p: string, planId: string) =>
    request<ApplySuggestionsResult>(`${base(p)}/plans/${planId}/suggestions:apply`, {
      method: 'POST',
    }),
  progress: (p: string, planId: string) =>
    request<PlanProgress>(`${base(p)}/plans/${planId}/progress`),
  // #35: eigener Aufruf, damit die Screenshots (data-URLs) erst beim Öffnen des Drill-downs laden.
  runHistory: (p: string, planId: string, testCaseId: string) =>
    request<RunHistoryEntry[]>(`${base(p)}/plans/${planId}/testcases/${testCaseId}/runs`),
  // Task 19, Step 1: projekt-gescopte Mitgliederliste (Tester-Auswahl in der ScopeTable).
  members: (p: string) => request<ProjectMember[]>(`${base(p)}/members`),
  // #91: Sammelzuweisung über die Scope-Tabellen-Aktionsleiste — EIN Request statt N
  // Einzel-PATCHes. `tester_id: null` entfernt den Tester bei allen übergebenen Testfällen.
  // Server-Vertrag (Task 10/11): `updated + skipped.length` === deduplizierte Eingabe; Reason-Code
  // `not_in_plan` heißt "nicht mehr im Plan" — der Testfall ist aus dem Scope gefallen, seit die
  // Auswahl getroffen wurde.
  bulkAssign: (
    p: string,
    planId: string,
    body: { test_case_ids: string[]; tester_id: string | null },
  ) =>
    request<{ updated: number; skipped: { test_case_id: string; reason: string }[] }>(
      `${base(p)}/plans/${planId}/assignments:bulk`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
};

// #114: Der 409-Body von PATCH …/status steckt als Text in ApiError.message (Muster wie
// parseNodeDependents in structure-api.ts). Liefert die offenen Knoten, wenn der Fehler genau
// dieser Konflikt ist — sonst null, damit der Aufrufer ihn wie jeden anderen Fehler behandelt.
// 2. Abnahme, Befund 1: der blockierende Plan aus der 409-Antwort von PATCH …/status. Ohne ihn
// stand dort nur „ein anderer Plan ist aktiv" — bei zwei aktiven Plänen (Migration 0011) genau
// die Information, die zum Auflösen fehlte.
export interface ActivePlanConflict {
  test_plan_id: string;
  name: string;
}

export function parseActivePlanConflict(err: unknown): ActivePlanConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  try {
    const body = JSON.parse(err.message) as { error?: string; active_plan?: ActivePlanConflict };
    if (body.error === 'another_plan_active' && body.active_plan) return body.active_plan;
  } catch {
    // kein JSON-Body -> ein anderer 409
  }
  return null;
}

// 2. Abnahme, Befund 1+2: die Nachweiszahlen aus den beiden 409-Antworten, die auf derselben
// Rechnung sitzen (`countPlanEvidence` im Server) — Löschen (`plan_has_evidence`) und Rücknahme
// der Aktivierung (`plan_reset_has_evidence`). Der Code bleibt im Ergebnis stehen, weil beide
// Fälle DIESELBE Ursache, aber einen ANDEREN Rat haben (abschließen vs. gar nichts).
export interface PlanEvidenceConflict {
  error: 'plan_has_evidence' | 'plan_reset_has_evidence';
  run_count: number;
  signoff_count: number;
}

export function parsePlanEvidence(err: unknown): PlanEvidenceConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  try {
    const body = JSON.parse(err.message) as Partial<PlanEvidenceConflict>;
    if (
      (body.error === 'plan_has_evidence' || body.error === 'plan_reset_has_evidence') &&
      typeof body.run_count === 'number' &&
      typeof body.signoff_count === 'number'
    ) {
      return { error: body.error, run_count: body.run_count, signoff_count: body.signoff_count };
    }
  } catch {
    // kein JSON-Body -> ein anderer 409
  }
  return null;
}

export function parseOpenSignoffNodes(err: unknown): OpenSignoffNode[] | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  try {
    const body = JSON.parse(err.message) as { error?: string; open_nodes?: OpenSignoffNode[] };
    if (body.error === 'signoffs_open' && Array.isArray(body.open_nodes)) return body.open_nodes;
  } catch {
    // kein JSON-Body -> ein anderer 409 (z. B. plan_closed), nicht dieser Konflikt
  }
  return null;
}
